import apiClient from './client';

export type ScalingMode = 'linear' | 'reduced' | 'fixed';

export interface Ingredient {
  name: string;
  amount: number | null;
  unit: string | null;
  notes: string | null;
  scaling?: ScalingMode | null;
}

export interface Instruction {
  step: number;
  text: string;
  timer: number | null;
}

export interface Recipe {
  id: string;
  title: string;
  description?: string;
  prep_time_minutes: number;
  cook_time_minutes: number;
  servings: number | null;
  difficulty: 'easy' | 'medium' | 'hard';
  cuisines: string[];
  seasons: string[];
  recipe_types: string[];
  ingredients: Ingredient[];
  instructions: Instruction[];
  notes: string[];
  image_key: string | null;
  is_favorite: boolean;
  is_quick_meal: boolean;
  is_public: boolean;
  calories?: number;
  created_at: string;
  updated_at: string;
}

export interface RecipeListParams {
  cuisine?: string;
  season?: string;
  recipeType?: string;
  difficulty?: string;
  maxPrepTime?: string;
  sort?: string;
  favorites?: string;
  quickMeals?: string;
  limit?: number;
}

export interface RecipeCreateInput {
  title: string;
  description?: string;
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  servings?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  cuisines?: string[];
  seasons?: string[];
  recipe_types?: string[];
  image_key?: string | null;
  notes?: string[];
  calories?: number;
}

export type RecipeUpdateInput = Partial<RecipeCreateInput>;

export const recipesApi = {
  list: async (params: RecipeListParams = {}) => {
    const searchParams = new URLSearchParams();
    if (params.cuisine) searchParams.set('cuisine', params.cuisine);
    if (params.season) searchParams.set('season', params.season);
    if (params.recipeType) searchParams.set('recipeType', params.recipeType);
    if (params.difficulty) searchParams.set('difficulty', params.difficulty);
    if (params.maxPrepTime) searchParams.set('maxPrepTime', params.maxPrepTime);
    if (params.sort) searchParams.set('sort', params.sort);
    if (params.favorites) searchParams.set('favorites', params.favorites);
    if (params.quickMeals) searchParams.set('quickMeals', params.quickMeals);
    if (params.limit) searchParams.set('limit', params.limit.toString());

    const query = searchParams.toString();
    const response = await apiClient.get<{ data: { recipes: Recipe[] } }>(`/recipes${query ? '?' + query : ''}`);
    return response.data.data.recipes;
  },

  search: async (query: string) => {
    const response = await apiClient.get<{ data: { recipes: Recipe[] } }>(
      `/recipes/search?q=${encodeURIComponent(query)}`,
    );
    return response.data.data.recipes;
  },

  get: async (id: string) => {
    const response = await apiClient.get<{ data: Recipe }>(`/recipes/${id}`);
    return response.data.data;
  },

  create: async (data: RecipeCreateInput) => {
    const response = await apiClient.post<{ data: Recipe }>('/recipes', data);
    return response.data.data;
  },

  update: async (id: string, data: RecipeUpdateInput) => {
    const response = await apiClient.put<{ data: Recipe }>(`/recipes/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string) => {
    await apiClient.delete(`/recipes/${id}`);
  },

  toggleFavorite: async (id: string) => {
    const response = await apiClient.patch<{ data: { is_favorite: boolean } }>(`/recipes/${id}/favorite`);
    return response.data.data;
  },

  toggleQuickMeal: async (id: string) => {
    const response = await apiClient.patch<{
      data: { is_quick_meal: boolean };
    }>(`/recipes/${id}/quick-meal`);
    return response.data.data;
  },

  updateIngredients: async (id: string, ingredients: Ingredient[]) => {
    const response = await apiClient.put<{ data: Recipe }>(`/recipes/${id}/ingredients`, {
      ingredients,
    });
    return response.data.data;
  },

  updateInstructions: async (id: string, instructions: Instruction[]) => {
    const response = await apiClient.put<{ data: Recipe }>(`/recipes/${id}/instructions`, {
      instructions,
    });
    return response.data.data;
  },
};
